import EntryFilters from "@app/components/entry-filters.tsx";
import EntryTable from "@app/components/entry-table.tsx";
import AddReceiptModal from "@app/components/modals/entries/add-receipt-modal.tsx";
import EntryReportModal from "@app/components/modals/entries/entry-report-modal.tsx";
import ViewReceiptModal from "@app/components/modals/entries/view-receipt-modal.tsx";
import PageTitle from "@app/components/page-title.tsx";
import { ActiveEnums } from "@app/data/engine.ts";
import useEntriesData from "@app/data-hooks/use-entries-data.ts";
import usePowerBiReportsData from "@app/data-hooks/use-power-bi-reports-data.ts";
import useAuth from "@app/hooks/use-auth.ts";
import { entryFiltersStore } from "@app/stores/filters.store.ts";
import { Button, FormControlLabel, Stack, Switch, Checkbox } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { useEffect, useState, useMemo } from "react";
import { defaultEntryFilters } from "@app/constants/filters.defaults";

export const Route = createFileRoute("/_authenticated/entry/snapshot")({
    component: Component,
});

function Component() {
    const { userData } = useAuth();

    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [entryId, setEntryId] = useState<string>();
    const [viewReceiptOpen, setViewReceiptOpen] = useState(false);
    const [addReceiptOpen, setAddReceiptOpen] = useState(false);
    const [rateType, setRateType] = useState<"billing" | "cost" | "internal">("cost");
    const [postingMode, setPostingMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [selectAll, setSelectAll] = useState(false); // New state for Select All

    const filters = useStore(entryFiltersStore);

    const { postEntries } = useEntriesData({ filters });
    const { generateProjectManagementPmJustPostedChargesReport } = usePowerBiReportsData();

    // Fetch entries for all categories to determine Select All state
    const { entriesSearchList: entriesCat1 } = useEntriesData({ filters: { ...filters, catId: 1 } });
    const { entriesSearchList: entriesCat2 } = useEntriesData({ filters: { ...filters, catId: 2 } });
    const { entriesSearchList: entriesCat3 } = useEntriesData({ filters: { ...filters, catId: 3 } });
    const { entriesSearchList: entriesCat4 } = useEntriesData({ filters: { ...filters, catId: 4 } });

    // Compute all entry IDs across all categories
    const allEntryIds = useMemo(() => {
        const ids = [
            ...(entriesCat1.data?.map((x) => x.entryId!) ?? []),
            ...(entriesCat2.data?.map((x) => x.entryId!) ?? []),
            ...(entriesCat3.data?.map((x) => x.entryId!) ?? []),
            ...(entriesCat4.data?.map((x) => x.entryId!) ?? []),
        ];
        return [...new Set(ids)]; // Ensure unique IDs
    }, [entriesCat1.data, entriesCat2.data, entriesCat3.data, entriesCat4.data]);

    // Update Select All state based on selectedIds
    useEffect(() => {
        if (allEntryIds.length > 0 && selectedIds.length === allEntryIds.length) {
            setSelectAll(true);
        } else {
            setSelectAll(false);
        }
    }, [selectedIds, allEntryIds]);

    // Handle Select All toggle
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedIds([]);
            setSelectAll(false);
        } else {
            setSelectedIds(allEntryIds);
            setSelectAll(true);
        }
    };

    useEffect(() => {
        entryFiltersStore.setState(() => ({
            ...defaultEntryFilters,
            staffId: userData?.UserId?.toString() ?? undefined,
            isPosted: postingMode ? ActiveEnums.NotActive : defaultEntryFilters.isPosted,
        }));
    }, [userData?.UserId]);

    const generatePostingReport = async () => {
        await generateProjectManagementPmJustPostedChargesReport.mutateAsync({
            amountTypeId: 1,
            entryIds: selectedIds,
        });
    };

    const handlePost = async () => {
        await postEntries.mutateAsync(selectedIds);
        await generatePostingReport();
        setSelectedIds([]);
        setSelectAll(false);
    };

    return (
        <>
            <EntryReportModal open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} filters={filters} />
            <ViewReceiptModal
                open={viewReceiptOpen}
                onClose={() => {
                    setEntryId(undefined);
                    setViewReceiptOpen(false);
                }}
                id={entryId}
            />
            <AddReceiptModal open={addReceiptOpen} onClose={() => setAddReceiptOpen(false)} filters={filters} />
            <PageTitle suppressBackButton>Snapshot Entry Management</PageTitle>
            <EntryFilters
                filters={filters}
                setFilters={entryFiltersStore.setState}
                postingMode={postingMode}
                rateType={rateType}
                setRateType={setRateType}
            />
            <Stack direction={"row"} spacing={2} pb={2}>
                <Button onClick={() => setReportDialogOpen(true)} variant={"contained"}>
                    Print Report
                </Button>
                <Button onClick={() => setAddReceiptOpen(true)} variant={"contained"}>
                    Upload Receipts
                </Button>
                <FormControlLabel
                    control={
                        <Switch
                            checked={postingMode}
                            onChange={(e) => {
                                setPostingMode(e.target.checked);
                                setSelectedIds([]); 
                                setSelectAll(false);
                                entryFiltersStore.setState(() => ({
                                    ...filters,
                                    isPosted: e.target.checked ? ActiveEnums.NotActive : ActiveEnums.All,
                                }));
                            }}
                        />
                    }
                    label={"Posting Mode"}
                />
                {postingMode && (
                    <>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                    disabled={allEntryIds.length === 0}
                                />
                            }
                            label={`Select All (${allEntryIds.length})`}
                        />
                        <Button variant={"contained"} onClick={handlePost} disabled={selectedIds.length === 0}>
                            Post Entries ({selectedIds.length})
                        </Button>
                        <Button
                            variant={"contained"}
                            onClick={generatePostingReport}
                            disabled={selectedIds.length === 0}
                        >
                            Preview Report ({selectedIds.length})
                        </Button>
                    </>
                )}
            </Stack>
            <Stack spacing={3}>
                {[1, 2, 3, 4].map((catId) => (
                    <EntryTable
                        key={catId}
                        catId={catId}
                        onShowReceipt={(id) => {
                            setViewReceiptOpen(true);
                            setEntryId(id);
                        }}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                        postingMode={postingMode}
                        rateType={rateType}
                        selectAll={selectAll}
                    />
                ))}
            </Stack>
        </>
    );
}